import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import {
  PROMO_LINE_PLACEHOLDER_CLASS,
  REAL_PROMO_LINE_SELECTOR,
  applyPromoLineToElement,
  parseListAttr,
  readPromoRules,
  resolvePromo,
} from '@theme/promo-rules';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * 3. PIV2 variant picker (client-side pricing payload, no section fetch)
 */
/**
 * Hide/show with the `hidden` attribute AND an inline `display`, because the
 * attribute alone does not survive this theme's CSS: an author rule setting
 * `display` outranks the UA's `[hidden] { display: none }`. Two rules in the
 * price block do exactly that — `.price-group-wrapper *` (custom-styling.css)
 * over the compare-at group, and `.price__discount-code-line` itself
 * (base.css). Both now carry `:not([hidden])`; the inline pin is the
 * belt-and-braces guarantee for any rule added later.
 *
 * @param {Element | null | undefined} el
 * @param {boolean} hidden
 */
function setPriceNodeHidden(el, hidden) {
  if (!(el instanceof HTMLElement)) return;
  el.hidden = hidden;
  if (hidden) el.style.display = 'none';
  else el.style.removeProperty('display');
}

class ProductPrice extends HTMLElement {
  /**
   * The section the listeners were attached to. Cached because `closest()`
   * returns null once the element is detached, which used to leak both
   * listeners on every disconnect.
   * @type {Element | null}
   */
  #listenerHost = null;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    this.#listenerHost = closestSection;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('bundle:price-update', this.updateBundlePrice);
  }

  disconnectedCallback() {
    const host = this.#listenerHost;
    this.#listenerHost = null;
    if (!host) return;
    host.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    host.removeEventListener('bundle:price-update', this.updateBundlePrice);
  }

  /**
   * Bundle pages (blocks/bundle-picker.liquid) price the PAGE as the sum of
   * the selected parts. The part pickers' own `variant:update` events are
   * stopped at the bundle root — each one carries a single child's price and
   * would otherwise be rendered here as the page price — and the bundle root
   * broadcasts the aggregate through this dedicated event instead of a
   * synthetic `variant:update`, which document-level listeners (Elevar
   * view-item, wishlist) would treat as a real variant change.
   *
   * @param {CustomEvent<{ productId?: string, pricing?: { price: number, compareAtPrice?: number } }>} event
   */
  updateBundlePrice = (event) => {
    const detail = event.detail || {};
    const productId = detail.productId != null ? String(detail.productId) : '';
    // Strict match: blocks/price.liquid always stamps data-product-id, so a
    // price element without one is some other card in the section (shop the
    // look, quick-add header) and must never adopt the bundle total.
    if (!productId || !this.dataset.productId || productId !== this.dataset.productId) return;
    if (!detail.pricing || typeof detail.pricing.price !== 'number') return;

    // Suit promo (blocks/bundle-picker.liquid getPartPromo): the regular suit
    // price, or its markdown compare-at when higher, is struck through and the
    // discounted suit takes its place, with the parts' offers as the note.
    const promo = detail.promo;
    if (promo && typeof promo.price === 'number' && promo.price < detail.pricing.price) {
      this.#updateFromPiv2Pricing(
        {
          price: promo.price,
          compareAtPrice: Math.max(Number(detail.pricing.compareAtPrice) || 0, detail.pricing.price),
        },
        promo
      );
      return;
    }

    this.#updateFromPiv2Pricing(detail.pricing);
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId && !this.dataset.productId) {
      this.dataset.productId = event.detail.data.productId;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    if (event.detail?.data?.source === 'piv2' && event.detail.data.piv2Pricing) {
      this.#updateFromPiv2Pricing(event.detail.data.piv2Pricing);
      return;
    }

    const html = event.detail.data.html;
    if (!html) return;

    // Find the new product-price element in the updated HTML
    let newProductPrice = null;

    if (this.dataset.blockId) {
      newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    }

    if (!newProductPrice && this.closest('.quick-add-product-details')) {
      newProductPrice = html.querySelector('.quick-add-product-details product-price');
    }

    if (!newProductPrice && this.dataset.productId) {
      newProductPrice = html.querySelector(`product-price[data-product-id="${this.dataset.productId}"]`);
    }

    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };

  #formatMoney(/** @type {number} */ amountInCents) {
    if (typeof amountInCents !== 'number' || Number.isNaN(amountInCents)) return '';

    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
    }).format(amountInCents / 100);
  }

  /**
   * Rebuild the "$X with code" line for the current price from the page's
   * "Discount Code Price" offers (@theme/promo-rules, fed by #promo-rules-json).
   * The per-product input comes off this element: data-product-tags (offer
   * scope), rewritten by variant-picker-v2 #applySiblingPromoState on a
   * combined-listing sibling swap. An absent attribute means "no tags", so
   * surfaces that never carry it are unchanged. data-show-discount-code-price
   * is the master flag Liquid stamps only when the feature is on.
   */
  #updateDiscountLine(/** @type {number} */ priceRaw) {
    let line = this.querySelector(REAL_PROMO_LINE_SELECTOR);

    if (this.dataset.showDiscountCodePrice !== 'true' || priceRaw <= 0) {
      setPriceNodeHidden(line, true);
      return;
    }

    const promo = resolvePromo(readPromoRules(), {
      priceCents: priceRaw,
      tags: parseListAttr(this.dataset.productTags),
    });
    if (!promo) {
      setPriceNodeHidden(line, true);
      return;
    }

    // A sibling swap can land on a product whose SSR matched no offer and
    // therefore rendered no line at all — build it in place.
    if (!(line instanceof HTMLElement)) {
      line = this.#createDiscountLine();
      if (!line) return;
    }

    setPriceNodeHidden(line, false);
    applyPromoLineToElement(line, promo, this.#formatMoney(promo.discountedCents));
  }

  /**
   * Client-side twin of the snippets/price.liquid markup: the line lives in
   * [ref="priceContainer"] as a sibling immediately after .price-group-wrapper
   * (before any trailing compare-at group). Never built on volume-pricing
   * containers or before a price exists to anchor to.
   */
  #createDiscountLine() {
    const container = this.querySelector('[ref="priceContainer"]');
    if (!(container instanceof HTMLElement)) return null;
    if (container.querySelector('.price-range')) return null;
    const wrapper = container.querySelector('.price-group-wrapper');
    if (!(wrapper instanceof HTMLElement)) return null;

    // The real line takes over the row a placeholder was holding.
    container.querySelectorAll(`.${PROMO_LINE_PLACEHOLDER_CLASS}`).forEach((ghost) => ghost.remove());

    const line = document.createElement('span');
    line.className = 'price__discount-code-line';
    const amountEl = document.createElement('span');
    amountEl.className = 'price__discount-code-amount';
    const textEl = document.createElement('span');
    textEl.className = 'price__discount-code-text';
    // A real space: the line is one run of inline text (no flex gap).
    line.append(amountEl, ' ', textEl);
    wrapper.insertAdjacentElement('afterend', line);
    return line;
  }

  /**
   * The suit promo note after the price, coloured by the offer's slot like the
   * "$X with code" line, ending in "with code X" when an offer needs a code.
   * A null promo removes it, so every other price update clears a note a
   * previous suit selection left behind.
   * @param {HTMLElement} container
   * @param {{ note: string, code?: string, slot: number } | null} promo
   */
  #applyBundlePromo(container, promo) {
    let note = container.querySelector('.price__bundle-promo-note');
    this.classList.toggle('price--bundle-promo', Boolean(promo));
    if (!promo) {
      this.style.removeProperty('--promo-discount-color');
      note?.remove();
      return;
    }

    this.style.setProperty('--promo-discount-color', promo.color || `var(--promo-discount-color-${promo.slot})`);
    if (!note) {
      note = document.createElement('span');
      note.className = 'price__bundle-promo-note';
      container.querySelector('.price-group-wrapper')?.insertAdjacentElement('afterend', note);
    }
    note.textContent = promo.note;
    if (promo.code) {
      const code = document.createElement('span');
      code.className = 'discount-code';
      code.textContent = promo.code;
      note.append(' with code ', code);
    }
  }

  /**
   * @param {{ price: number, priceFormatted?: string, compareAtPrice?: number, compareAtPriceFormatted?: string }} pricing
   * @param {{ note: string, code?: string, slot: number } | null} [bundlePromo]
   */
  #updateFromPiv2Pricing(pricing, bundlePromo = null) {
    const container = this.querySelector('[ref="priceContainer"]');
    if (!(container instanceof HTMLElement)) return;

    if (container.querySelector('.price-range')) return;

    const price = pricing.price;
    const compareAt = Number(pricing.compareAtPrice) || 0;
    const priceFormatted = pricing.priceFormatted || this.#formatMoney(price);
    const compareFormatted =
      pricing.compareAtPriceFormatted || (compareAt > 0 ? this.#formatMoney(compareAt) : '');
    const onSale = compareAt > price;


    container.querySelectorAll('.price').forEach((el) => {
      if (el.classList.contains('price-range')) return;
      el.textContent = priceFormatted;
      el.classList.toggle('custom-price--highlight', onSale && !bundlePromo);
    });

    if (onSale && compareFormatted && !container.querySelector('.compare-at-price')) {
      this.#ensureCompareAtInContainer(container, compareFormatted);
    }

    container.querySelectorAll('.compare-at-price').forEach((el) => {
      if (onSale) el.textContent = compareFormatted;
      this.#setCompareAtVisible(el, onSale);
    });

    this.#applyBundlePromo(container, bundlePromo);
    if (bundlePromo) {
      setPriceNodeHidden(this.querySelector(REAL_PROMO_LINE_SELECTOR), true);
    } else {
      this.#updateDiscountLine(price);
    }
  }

  /**
   * Show/hide a compare-at price with the `hidden` + inline-display pair
   * quick-add.js uses on these same nodes, so the two can hand off freely.
   *
   * The attribute alone is not enough: `.price-group-wrapper *` (custom-styling.css,
   * <=1023px) and `.product-information-v2 .compare-at-price` (product-information-v2.css)
   * set `display`, and an author rule beats the UA `[hidden]` rule — without the
   * inline pin, a sale -> full-price switch kept advertising the previous
   * strikethrough on mobile. Both rules now carry `:not([hidden])` as well; the
   * inline style stays as the viewport-independent guarantee.
   *
   * Falls back to the element itself when Liquid rendered no `role="group"`
   * wrapper, so the hide can never silently no-op.
   *
   * @param {Element} compareEl
   * @param {boolean} visible
   */
  #setCompareAtVisible(compareEl, visible) {
    const group = compareEl.closest('[role="group"]');
    const target = group instanceof HTMLElement ? group : compareEl;
    if (!(target instanceof HTMLElement)) return;

    setPriceNodeHidden(target, !visible);
  }

  /**
   * SSR may omit compare-at when the initial variant is not on sale; inject markup
   * that mirrors snippets/price.liquid when PIV2 switches to a sale variant.
   * @param {HTMLElement} container
   * @param {string} compareFormatted
   */
  #ensureCompareAtInContainer(container, compareFormatted) {
    const showSalePriceFirst = this.dataset.showSalePriceFirst === 'true';
    const compareGroup = this.#createCompareAtGroup(compareFormatted);

    if (showSalePriceFirst) {
      const discountLine = container.querySelector(REAL_PROMO_LINE_SELECTOR);
      if (discountLine instanceof HTMLElement) {
        discountLine.insertAdjacentElement('afterend', compareGroup);
      } else {
        container.appendChild(compareGroup);
      }
      return;
    }

    const wrapper = container.querySelector('.price-group-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    const priceEl = wrapper.querySelector('.price:not(.price-range)');
    if (priceEl && !priceEl.closest('[role="group"]')) {
      const saleGroup = document.createElement('span');
      saleGroup.setAttribute('role', 'group');
      const saleLabel = document.createElement('span');
      saleLabel.className = 'visually-hidden';
      saleLabel.textContent = 'Sale price\u00a0';
      saleGroup.appendChild(saleLabel);
      priceEl.parentNode?.insertBefore(saleGroup, priceEl);
      saleGroup.appendChild(priceEl);
    }

    wrapper.insertBefore(compareGroup, wrapper.firstChild);
  }

  /**
   * @param {string} compareFormatted
   * @returns {HTMLSpanElement}
   */
  #createCompareAtGroup(compareFormatted) {
    const compareGroup = document.createElement('span');
    compareGroup.setAttribute('role', 'group');
    const regularLabel = document.createElement('span');
    regularLabel.className = 'visually-hidden';
    regularLabel.textContent = 'Regular price\u00a0';
    const compareEl = document.createElement('span');
    compareEl.className = 'compare-at-price';
    compareEl.textContent = compareFormatted;
    compareGroup.appendChild(regularLabel);
    compareGroup.appendChild(compareEl);
    return compareGroup;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
